import { Link } from 'react-router-dom';
import './Legal.css';

const LAST_UPDATED = '1 March 2025';
const COMPANY = 'SBH The Company';
const EMAIL = 'privacy@transportily.app';

export default function Privacy() {
  return (
    <div className="page legal-page">
      <div className="container legal-container">
        <div className="legal-header">
          <Link to="/" className="auth-logo" style={{ marginBottom: '24px', display: 'inline-block' }}>
            Transport<span>ily</span>
          </Link>
          <h1>Privacy Policy</h1>
          <p className="legal-meta">Last updated: {LAST_UPDATED} · GDPR compliant</p>
        </div>

        <div className="legal-body">

          <section>
            <h2>1. Controller</h2>
            <p>
              The data controller responsible for the processing of your personal data is:<br /><br />
              <strong>{COMPANY}</strong><br />
              Email: <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
            </p>
            <p>
              You have the right to contact us at any time regarding the processing of your personal data.
            </p>
          </section>

          <section>
            <h2>2. Data we collect and why</h2>

            <h3>2.1 Account data</h3>
            <p>When you register, we collect your <strong>full name, email address, and password hash</strong>. This is necessary to create and manage your account (<em>Art. 6(1)(b) GDPR</em> — performance of a contract).</p>

            <h3>2.2 Profile data</h3>
            <p>You may optionally provide your <strong>phone number, WhatsApp number, city, country, country of origin, languages spoken, bio, and profile photo</strong>. This information is used to facilitate trust between users and improve matching. Legal basis: <em>Art. 6(1)(a) GDPR</em> — your consent (you choose to provide it).</p>

            <h3>2.3 Trip and booking data</h3>
            <p>We collect information about <strong>trips posted, bookings made, shipment requests, and booking statuses</strong>. This is necessary to operate the marketplace (<em>Art. 6(1)(b) GDPR</em>).</p>

            <h3>2.4 Payment data</h3>
            <p>Payments are processed by <strong>Stripe, Inc.</strong> We do not store full card numbers. We receive and store transaction IDs, amounts, and payment status. Legal basis: <em>Art. 6(1)(b) GDPR</em>.</p>

            <h3>2.5 Communications</h3>
            <p>We store <strong>in-platform chat messages</strong> between users to enable the messaging feature and resolve disputes. Legal basis: <em>Art. 6(1)(b) GDPR</em>.</p>

            <h3>2.6 Reviews</h3>
            <p>Reviews you write and receive are stored and displayed publicly on user profiles. Legal basis: <em>Art. 6(1)(b) GDPR</em>.</p>

            <h3>2.7 Technical data</h3>
            <p>We collect <strong>IP addresses, browser type, device information, and usage logs</strong> for security, fraud prevention, and platform stability. Legal basis: <em>Art. 6(1)(f) GDPR</em> — legitimate interests.</p>

            <h3>2.8 Email notifications</h3>
            <p>We send transactional emails (booking confirmations, proposals, status updates) via <strong>Resend</strong>. Legal basis: <em>Art. 6(1)(b) GDPR</em>.</p>
          </section>

          <section>
            <h2>3. Data sharing</h2>
            <p>We share your data only as necessary:</p>
            <ul>
              <li><strong>Other users:</strong> Your name, rating, city, and languages are visible to other users. Trip details and booking information are shared with the relevant counterparty.</li>
              <li><strong>Stripe:</strong> Payment processing. <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer">Stripe Privacy Policy</a>.</li>
              <li><strong>Supabase:</strong> Database and authentication infrastructure. Data is stored on servers within the EU.</li>
              <li><strong>Resend:</strong> Transactional email delivery.</li>
              <li><strong>Legal authorities:</strong> We may disclose data when required by law, court order, or to protect the rights and safety of users.</li>
            </ul>
            <p>We do not sell your data to third parties. We do not use your data for advertising.</p>
          </section>

          <section>
            <h2>4. International transfers</h2>
            <p>
              Some of our service providers (Stripe, Resend) may process data in the United States. Where data is transferred outside the EU/EEA, we ensure appropriate safeguards are in place, including Standard Contractual Clauses (<em>Art. 46 GDPR</em>).
            </p>
          </section>

          <section>
            <h2>5. Data retention</h2>
            <ul>
              <li><strong>Account data:</strong> Retained for as long as your account is active. Deleted within 30 days of account closure, except where retention is required by law.</li>
              <li><strong>Transaction data:</strong> Retained for <strong>10 years</strong> as required by German commercial and tax law (<em>§ 257 HGB, § 147 AO</em>).</li>
              <li><strong>Chat messages:</strong> Retained for 2 years after the related booking is completed or cancelled.</li>
              <li><strong>Logs:</strong> Retained for up to 90 days.</li>
            </ul>
          </section>

          <section>
            <h2>6. Your rights under GDPR</h2>
            <p>You have the following rights regarding your personal data:</p>
            <ul>
              <li><strong>Right of access</strong> (<em>Art. 15 GDPR</em>): Request a copy of all data we hold about you.</li>
              <li><strong>Right to rectification</strong> (<em>Art. 16 GDPR</em>): Request correction of inaccurate data.</li>
              <li><strong>Right to erasure</strong> (<em>Art. 17 GDPR</em>): Request deletion of your data ("right to be forgotten"), subject to legal retention obligations.</li>
              <li><strong>Right to restriction</strong> (<em>Art. 18 GDPR</em>): Request that we limit processing of your data.</li>
              <li><strong>Right to data portability</strong> (<em>Art. 20 GDPR</em>): Receive your data in a machine-readable format.</li>
              <li><strong>Right to object</strong> (<em>Art. 21 GDPR</em>): Object to processing based on legitimate interests.</li>
              <li><strong>Right to withdraw consent</strong> (<em>Art. 7(3) GDPR</em>): Withdraw consent at any time without affecting prior processing.</li>
            </ul>
            <p>
              To exercise any of these rights, contact us at <a href={`mailto:${EMAIL}`}>{EMAIL}</a>. We will respond within <strong>30 days</strong>.
            </p>
            <p>
              You also have the right to lodge a complaint with the competent data protection authority. In Germany, this is the <strong>Berliner Beauftragte für Datenschutz und Informationsfreiheit</strong> (<a href="https://www.datenschutz-berlin.de" target="_blank" rel="noreferrer">datenschutz-berlin.de</a>).
            </p>
          </section>

          <section>
            <h2>7. Cookies and tracking</h2>
            <p>
              We use only <strong>technically necessary cookies</strong> for authentication and session management. We do not use tracking cookies, advertising cookies, or third-party analytics cookies that require consent under the <em>Telekommunikation-Digitale-Dienste-Datenschutz-Gesetz (TDDDG)</em>.
            </p>
          </section>

          <section>
            <h2>8. Security</h2>
            <p>
              We implement appropriate technical and organisational security measures to protect your data against unauthorised access, loss, or disclosure. These include encrypted connections (TLS), password hashing, and access controls. However, no system is 100% secure and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2>9. Children</h2>
            <p>
              The Platform is not intended for persons under 18. We do not knowingly collect data from minors. If you believe a minor has created an account, please contact us and we will delete it promptly.
            </p>
          </section>

          <section>
            <h2>10. Changes to this Policy</h2>
            <p>
              We may update this Privacy Policy. We will notify you of material changes via email or in-app notification. The date of the last update is shown at the top of this page.
            </p>
          </section>

          <section>
            <h2>11. Contact</h2>
            <p>
              {COMPANY}<br />
              Email: <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
            </p>
          </section>

        </div>

        <div className="legal-footer">
          <Link to="/terms">Terms of Service</Link>
          <span>·</span>
          <Link to="/">Back to Transportily</Link>
        </div>
      </div>
    </div>
  );
}